import React, { ReactNode, useMemo } from 'react'
import { Typography, Chip, Box, Link, useMediaQuery, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  CalendarMonthOutlined,
  InfoOutlined,
  MeetingRoomOutlined
} from '@mui/icons-material'
import dayjs from 'dayjs'
import { Reservation } from 'services/commonRooms/commonRooms.hooks'
import CancelReservation from './CancelReservation'
import {
  RESERVATION_STATUS_META,
  RESERVATION_STATUS
} from 'services/commonRooms/commonRooms.consts'
import { useIntl } from 'react-intl'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { getEquipmentList } from 'services/commonRooms/commonRooms.utils'
import {
  getReservationGradient,
  getReservationStatusColors
} from 'components/Helpdesk/helpdesk.utils'

// Reservation detail ("Panel Klienta 3.0" Rezerwacje tab):
// desktop detail panel with Termin / Szczegóły sections, mobile card.
const CommonRoomReservation: React.FC<{ reservation: Reservation }> = ({ reservation }) => {
  const {
    reservationCode,
    commonRoom,
    startDatetime,
    endDatetime,
    durationMinutes,
    purpose,
    specialRequirements,
    status,
    totalCostPln,
    totalCostEur,
    ruleAcceptance,
    id
  } = reservation
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { formatMessage } = useIntl()
  const { hasActiveLeases } = useLeaseContractsList()

  const equipmentList = useMemo(() => getEquipmentList(commonRoom), [commonRoom])
  const badgeColors = getReservationStatusColors(status, isDesktop)
  const canCancel =
    [RESERVATION_STATUS.PENDING, RESERVATION_STATUS.CONFIRMED].includes(status as any) &&
    hasActiveLeases

  const statusBadge = (
    <Box sx={{ backgroundColor: badgeColors.backgroundColor, padding: '3px 10px', flexShrink: 0 }}>
      <Typography
        sx={{
          color: badgeColors.color,
          fontSize: '11px',
          fontWeight: 700,
          lineHeight: 'normal',
          whiteSpace: 'nowrap'
        }}>
        {formatMessage(RESERVATION_STATUS_META[status].i18n)}
      </Typography>
    </Box>
  )

  const statusAlert =
    status === RESERVATION_STATUS.CONFIRMED
      ? {
          color: theme.r4r.green,
          titleColor: theme.r4r.green,
          title: formatMessage({
            id: 'helpdesk.reservationConfirmed',
            defaultMessage: 'Rezerwacja potwierdzona'
          }),
          text: formatMessage({
            id: 'helpdesk.reservationConfirmedHint',
            defaultMessage: 'Pamiętaj o punktualnym przybyciu.'
          })
        }
      : status === RESERVATION_STATUS.PENDING
      ? {
          color: theme.r4r.yellow,
          titleColor: theme.r4r.yellowDark,
          title: formatMessage({
            id: 'helpdesk.reservationPending',
            defaultMessage: 'Oczekuje na potwierdzenie'
          }),
          text: formatMessage({
            id: 'helpdesk.reservationPendingHint',
            defaultMessage:
              'Rezerwacja wymaga akceptacji przez administrację. Otrzymasz powiadomienie.'
          })
        }
      : null

  if (isDesktop) {
    const startDay = dayjs(startDatetime)
    const dateLabel = startDay.format('dddd, D MMMM YYYY')
    const durationLabel =
      durationMinutes % 60 === 0
        ? formatMessage(
            { id: 'helpdesk.durationHours', defaultMessage: '{count} godz.' },
            { count: durationMinutes / 60 }
          )
        : formatMessage(
            { id: 'helpdesk.durationMinutes', defaultMessage: '{count} min' },
            { count: durationMinutes }
          )

    const sectionTitle = (icon: ReactNode, label: string) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.r4r.navy }}>
        {icon}
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            color: theme.r4r.navy
          }}>
          {label}
        </Typography>
      </Box>
    )

    const dataRow = (label: string, value: ReactNode, isLast?: boolean) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '24px',
          padding: '10px 0 11px',
          borderBottom: isLast ? 'none' : `1px solid ${theme.r4r.greyLight}`
        }}>
        <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, flexShrink: 0 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: theme.r4r.ink,
            textAlign: 'right'
          }}>
          {value}
        </Typography>
      </Box>
    )

    const rows: Array<{ label: string; value: ReactNode }> = [
      {
        label: formatMessage({ id: 'helpdesk.place', defaultMessage: 'Miejsce' }),
        value: commonRoom.displayName
      },
      {
        label: formatMessage({ id: 'helpdesk.capacity', defaultMessage: 'Pojemność' }),
        value: formatMessage(
          { id: 'helpdesk.capacityValue', defaultMessage: 'do {count} osób' },
          { count: commonRoom.maxCapacity }
        )
      },
      {
        label: formatMessage({ id: 'helpdesk.area', defaultMessage: 'Powierzchnia' }),
        value: `${commonRoom.areaSqm} m²`
      },
      ...(equipmentList.length > 0
        ? [
            {
              label: formatMessage({ id: 'helpdesk.equipment', defaultMessage: 'Wyposażenie' }),
              value: equipmentList.join(', ')
            }
          ]
        : []),
      ...(purpose
        ? [
            {
              label: formatMessage({ id: 'helpdesk.purpose', defaultMessage: 'Cel' }),
              value: purpose
            }
          ]
        : []),
      ...(specialRequirements
        ? [
            {
              label: formatMessage({
                id: 'helpdesk.specialRequirements',
                defaultMessage: 'Wymagania specjalne'
              }),
              value: specialRequirements
            }
          ]
        : []),
      {
        label: formatMessage({ id: 'helpdesk.cost', defaultMessage: 'Koszt' }),
        value: `${totalCostPln} PLN / ${totalCostEur} EUR`
      }
    ]

    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '22px 28px 23px',
            borderBottom: `1px solid ${theme.r4r.greyLight}`
          }}>
          <Box
            sx={{
              width: '52px',
              height: '52px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: getReservationGradient(status),
              flexShrink: 0
            }}>
            <MeetingRoomOutlined sx={{ color: '#fff', fontSize: '24px' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 800,
                color: theme.r4r.navy,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              {commonRoom.displayName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '4px' }}>
              <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, whiteSpace: 'nowrap' }}>
                {formatMessage(
                  { id: 'helpdesk.reservationCode', defaultMessage: 'Rezerwacja #{code}' },
                  { code: reservationCode }
                )}{' '}
                ·
              </Typography>
              {statusBadge}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '20px 28px 21px',
            borderBottom: `1px solid ${theme.r4r.greyLight}`
          }}>
          {sectionTitle(
            <CalendarMonthOutlined sx={{ fontSize: '15px' }} />,
            formatMessage({ id: 'helpdesk.reservationTerm', defaultMessage: 'Termin' })
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              backgroundColor: alpha(theme.r4r.navy, 0.04),
              borderLeft: `3px solid ${theme.r4r.navy}`,
              padding: '16px 20px 16px 23px'
            }}>
            <Box
              sx={{
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.r4r.gradient,
                flexShrink: 0
              }}>
              <CalendarMonthOutlined sx={{ color: '#fff', fontSize: '20px' }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: theme.r4r.navy,
                  textTransform: 'capitalize'
                }}>
                {dateLabel}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, mt: '2px' }}>
                {dayjs(startDatetime).format('HH:mm')} – {dayjs(endDatetime).format('HH:mm')} (
                {durationLabel})
              </Typography>
            </Box>
          </Box>
          {statusAlert && (
            <Box
              sx={{
                backgroundColor: alpha(statusAlert.color, 0.08),
                borderLeft: `3px solid ${statusAlert.color}`,
                px: '16px',
                py: '12px'
              }}>
              <Typography
                sx={{ fontSize: '13px', fontWeight: 600, color: statusAlert.titleColor }}>
                {statusAlert.title}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px' }}>
                {statusAlert.text}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ padding: '20px 28px', flex: 1 }}>
          <Box sx={{ pb: '12px' }}>
            {sectionTitle(
              <InfoOutlined sx={{ fontSize: '15px' }} />,
              formatMessage({ id: 'helpdesk.detailsSection', defaultMessage: 'Szczegóły' })
            )}
          </Box>
          {rows.map((row, index) => (
            <React.Fragment key={row.label}>
              {dataRow(row.label, row.value, index === rows.length - 1)}
            </React.Fragment>
          ))}
          <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '12px' }}>
            <Link href={commonRoom.rulesPdfUrl} target='_blank' rel='noopener'>
              {formatMessage({ id: 'helpdesk.rules', defaultMessage: 'Regulamin' })}
            </Link>{' '}
            {formatMessage(
              { id: 'helpdesk.rulesAcceptedAt', defaultMessage: 'zaakceptowany: {date}' },
              { date: new Date(ruleAcceptance.acceptedAt).toLocaleString() }
            )}
          </Typography>
        </Box>

        {canCancel && (
          <Box
            sx={{
              borderTop: `1px solid ${theme.r4r.greyLight}`,
              padding: '19px 28px 18px',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
            <CancelReservation
              reservationId={id}
              buttonSx={{
                backgroundColor: 'transparent',
                border: `1px solid ${theme.r4r.greyLight}`,
                borderRadius: 0,
                color: theme.r4r.red,
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '13px',
                padding: '12px 21px',
                boxShadow: 'none',
                ':hover': {
                  backgroundColor: alpha(theme.r4r.red, 0.06),
                  boxShadow: 'none'
                }
              }}
            />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        boxShadow: theme.r4r.cardShadow,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column'
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: '56px',
            height: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(badgeColors.color, 0.15)
          }}>
          <MeetingRoomOutlined sx={{ color: badgeColors.color, fontSize: '26px' }} />
        </Box>
        {statusBadge}
      </Box>

      <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.r4r.navy, mt: '20px' }}>
        {commonRoom.displayName}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px' }}>
        {reservationCode}
      </Typography>

      <Box sx={{ display: 'flex', gap: '16px', mt: '16px' }}>
        <Box sx={{ flex: 1, backgroundColor: '#F9FAFB', px: '16px', py: '14px' }}>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: theme.r4r.grey
            }}>
            {formatMessage({ id: 'helpdesk.date', defaultMessage: 'Data' })}
          </Typography>
          <Typography
            sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy, mt: '4px' }}>
            {dayjs(startDatetime).format('DD.MM.YYYY')}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, backgroundColor: '#F9FAFB', px: '16px', py: '14px' }}>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: theme.r4r.grey
            }}>
            {formatMessage({ id: 'helpdesk.hours', defaultMessage: 'Godziny' })}
          </Typography>
          <Typography
            sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy, mt: '4px' }}>
            {dayjs(startDatetime).format('HH:mm')} – {dayjs(endDatetime).format('HH:mm')}
          </Typography>
        </Box>
      </Box>

      {statusAlert && (
        <Box
          sx={{
            backgroundColor: alpha(statusAlert.color, 0.08),
            borderLeft: `3px solid ${statusAlert.color}`,
            px: '16px',
            py: '14px',
            mt: '16px'
          }}>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: statusAlert.titleColor
            }}>
            {statusAlert.title}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '4px' }}>
            {statusAlert.text}
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {commonRoom.displayDescription && (
          <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>
            {commonRoom.displayDescription}
          </Typography>
        )}
        {equipmentList.length > 0 && (
          <Box sx={{ gap: 0.5, display: 'flex', flexWrap: 'wrap' }}>
            {equipmentList.map((item, idx) => (
              <Chip key={idx} label={item} size='small' />
            ))}
          </Box>
        )}
        <Typography sx={{ fontSize: '13px', color: theme.r4r.ink }}>
          Pojemność: {commonRoom.maxCapacity} · Powierzchnia: {commonRoom.areaSqm} m² · Czas
          trwania: {durationMinutes} min
        </Typography>
        {purpose && (
          <Typography sx={{ fontSize: '13px', color: theme.r4r.ink }}>Cel: {purpose}</Typography>
        )}
        {specialRequirements && (
          <Typography sx={{ fontSize: '13px', color: theme.r4r.ink }}>
            Wymagania specjalne: {specialRequirements}
          </Typography>
        )}
        <Typography sx={{ fontSize: '13px', color: theme.r4r.ink }}>
          Koszt: {totalCostPln} PLN / {totalCostEur} EUR
        </Typography>
        <Typography sx={{ fontSize: '12px', color: theme.r4r.grey }}>
          <Link href={commonRoom.rulesPdfUrl} target='_blank' rel='noopener'>
            Regulamin
          </Link>{' '}
          zaakceptowany: {new Date(ruleAcceptance.acceptedAt).toLocaleString()}
        </Typography>
      </Box>

      {canCancel && (
        <Box sx={{ borderTop: `1px solid ${theme.r4r.greyLight}`, mt: '20px', pt: '16px' }}>
          <CancelReservation reservationId={id} />
        </Box>
      )}
    </Box>
  )
}

export default CommonRoomReservation
